import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { StoreDispatch } from '../../store/configure-store'
import { Spinner } from '../../cmps/util-cmps/spinner'
import { FormModal } from '../../cmps/forms/modals/form-modal'
import { FormInputText } from '../../cmps/forms/input/form-input-text'
import { FormComboBox } from '../../cmps/forms/input/form-combobox'
import { utilService } from '../../services/util-service'
import { setUserMsg } from '../../store/user'
import { httpService } from '../../services/http-service'

interface AgentScriptHashEntry {
    id: number
    company_name?: string | null
    entity_name: string
    agent_name: string
    script_version: string
    script_hash: string
    implemented_at: string
    implemented_by: string
    is_active: boolean
    deactivated_at?: string | null
    replaced_by?: number | null
    created_at?: string | null
    updated_at?: string | null
}

type ApiListResponse = { items: AgentScriptHashEntry[] }
type ApiEntryResponse = { entry: AgentScriptHashEntry; replaced_active_entry?: AgentScriptHashEntry | null }
type ApiCreateConflictCandidate = { source_company_name: string }
type ApiCreateConflictResponse = {
    message?: string
    entity_name?: string
    candidates: ApiCreateConflictCandidate[]
}

type ModalMode = 'deactivate' | 'activate' | 'history' | null
type HashVariant = 'raw' | 'normalized'
type SortKey = 'source' | 'entity' | 'agent' | 'version' | 'hash' | 'implementedBy' | 'implementedAt' | 'status'
type SortDirection = 'asc' | 'desc'

interface HashPreviewState {
    fileName: string | null
    rawHash: string | null
    normalizedHash: string | null
    isCalculating: boolean
    error: string | null
    selectedVariant: HashVariant
}

const emptyForm = {
    entity_name: '',
    agent_name: '',
    script_version: '',
    script_hash: '',
}

const emptyHashPreview: HashPreviewState = {
    fileName: null,
    rawHash: null,
    normalizedHash: null,
    isCalculating: false,
    error: null,
    selectedVariant: 'normalized',
}

const getApiBaseUrl = () => {
    const apiBaseUrl = process.env.REACT_APP_ENTER_BACKEND_URL || process.env.REACT_APP_BACKEND_URL
    if (!apiBaseUrl) {
        throw new Error('Missing backend URL configuration')
    }
    return apiBaseUrl
}

const normalizeLineEndings = (value: string) => value.replace(/\r\n/g, '\n').replace(/\r/g, '\n')

const bufferToHex = (buffer: ArrayBuffer) => Array.from(new Uint8Array(buffer))
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('')

const sha256Hex = async (bytes: ArrayBuffer | Uint8Array) => {
    const digest = await crypto.subtle.digest('SHA-256', bytes)
    return bufferToHex(digest)
}

async function requestJson<T>(
    endpoint: string,
    method: 'GET' | 'POST' | 'PATCH',
    body?: Record<string, any>,
    queryParams?: Record<string, string | number | boolean | null | undefined>,
): Promise<T> {
    const accessToken = httpService.getAccessToken()
    const headers: Record<string, string> = {}
    if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`
    }
    const url = new URL(endpoint, getApiBaseUrl()).href
    const urlWithQuery = queryParams
        ? `${url}${url.includes('?') ? '&' : '?'}${new URLSearchParams(
            Object.entries(queryParams).reduce<Record<string, string>>((acc, [key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    acc[key] = String(value)
                }
                return acc
            }, {}),
        ).toString()}`
        : url
    const requestInit: RequestInit = { method, headers }
    if (method !== 'GET') {
        headers['Content-Type'] = 'application/json'
        requestInit.body = JSON.stringify(body || {})
    }

    if (method === 'GET') {
        const rawResponse = await fetch(urlWithQuery, requestInit)
        if (!rawResponse.ok) throw rawResponse
        return rawResponse.json()
    }

    const rawResponse = await fetch(urlWithQuery, requestInit)
    if (!rawResponse.ok) throw rawResponse
    return rawResponse.json()
}

const formatDateTime = (value?: string | null) => value ? utilService.formatDateTime(value) || '—' : '—'

const normalizePairKey = (value: string) => value.trim().toLowerCase()
const getDisplayCompanyName = (entry?: AgentScriptHashEntry | null) => entry?.company_name || 'auditech'

const getEntryOrderingValue = (entry: AgentScriptHashEntry) => {
    const timestamp = Date.parse(entry.updated_at || entry.implemented_at || entry.created_at || '')
    return Number.isFinite(timestamp) ? timestamp : 0
}

const isLaterEntry = (candidate: AgentScriptHashEntry, current: AgentScriptHashEntry) => {
    const candidateTime = getEntryOrderingValue(candidate)
    const currentTime = getEntryOrderingValue(current)

    if (candidateTime !== currentTime) {
        return candidateTime > currentTime
    }

    return candidate.id > current.id
}

const isPreferredDisplayEntry = (candidate: AgentScriptHashEntry, current: AgentScriptHashEntry) => {
    if (candidate.is_active !== current.is_active) {
        return candidate.is_active
    }

    return isLaterEntry(candidate, current)
}

export const AgentHashesPage: React.FC = () => {
    const dispatch = useDispatch<StoreDispatch>()

    const [items, setItems] = useState<AgentScriptHashEntry[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [hasError, setHasError] = useState(false)
    const [modalMode, setModalMode] = useState<ModalMode>(null)
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
    const [form, setForm] = useState(emptyForm)
    const [selectedEntry, setSelectedEntry] = useState<AgentScriptHashEntry | null>(null)
    const [historyItems, setHistoryItems] = useState<AgentScriptHashEntry[]>([])
    const [historyLoading, setHistoryLoading] = useState(false)
    const [historyError, setHistoryError] = useState(false)
    const [selectedActivationScriptId, setSelectedActivationScriptId] = useState<number | null>(null)
    const [deactivateReason, setDeactivateReason] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [hashPreview, setHashPreview] = useState<HashPreviewState>(emptyHashPreview)
    const [agentNameSuggestions, setAgentNameSuggestions] = useState<string[]>([])
    const [scriptVersionSuggestions, setScriptVersionSuggestions] = useState<string[]>([])
    const [createConflict, setCreateConflict] = useState<ApiCreateConflictResponse | null>(null)
    const [sortKey, setSortKey] = useState<SortKey>('entity')
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
    const fileInputRef = useRef<HTMLInputElement>(null)
    const itemsRequestSeqRef = useRef(0)
    const fileRequestSeqRef = useRef(0)
    const historyRequestSeqRef = useRef(0)

    const loadItems = useCallback(async () => {
        const requestSeq = ++itemsRequestSeqRef.current
        setIsLoading(true)
        setHasError(false)
        try {
            const result = await requestJson<ApiListResponse>('/api/backoffice/admin-page-agent-hashes', 'GET')
            if (requestSeq !== itemsRequestSeqRef.current) return
            setItems(result.items || [])
        } catch {
            if (requestSeq !== itemsRequestSeqRef.current) return
            setHasError(true)
        } finally {
            if (requestSeq !== itemsRequestSeqRef.current) return
            setIsLoading(false)
        }
    }, [])

    useEffect(() => {
        loadItems()
    }, [loadItems])

    useEffect(() => {
        let active = true
        const loadAgentNames = async () => {
            try {
                const result = await requestJson<{ items: string[] }>('/api/backoffice/admin-page-agent-hashes/agent-names', 'GET')
                if (!active) return
                setAgentNameSuggestions(Array.from(new Set(result.items || [])).filter(Boolean).sort((a, b) => a.localeCompare(b)))
            } catch {
                if (!active) return
                setAgentNameSuggestions([])
            }
        }
        loadAgentNames()
        return () => {
            active = false
        }
    }, [])

    useEffect(() => {
        let active = true
        const loadScriptVersions = async () => {
            try {
                const result = await requestJson<{ items: string[] }>('/api/backoffice/admin-page-agent-hashes/script-versions', 'GET')
                if (!active) return
                setScriptVersionSuggestions(Array.from(new Set(result.items || [])).filter(Boolean).sort((a, b) => a.localeCompare(b)))
            } catch {
                if (!active) return
                setScriptVersionSuggestions([])
            }
        }
        loadScriptVersions()
        return () => {
            active = false
        }
    }, [])

    const resetHashState = useCallback(() => {
        setHashPreview(emptyHashPreview)
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }, [])

    const openCreateModal = () => {
        setForm(emptyForm)
        setIsCreateModalOpen(true)
        setSelectedEntry(null)
        setDeactivateReason('')
        resetHashState()
    }

    const openDeactivateModal = (entry: AgentScriptHashEntry) => {
        if (!requireSourceCompanyName(entry)) return
        setSelectedEntry(entry)
        setDeactivateReason('')
        setModalMode('deactivate')
    }

    const openActivateModal = async (entry: AgentScriptHashEntry) => {
        const sourceCompanyName = requireSourceCompanyName(entry)
        if (!sourceCompanyName) return
        setSelectedEntry(entry)
        setDeactivateReason('')
        setSelectedActivationScriptId(entry.id)
        setModalMode('activate')
        const requestSeq = ++historyRequestSeqRef.current
        setHistoryItems([])
        setHistoryLoading(true)
        setHistoryError(false)
        try {
            const result = await requestJson<ApiListResponse>('/api/backoffice/admin-page-agent-hashes/history', 'GET', undefined, {
                source_company_name: sourceCompanyName,
                entity_name: entry.entity_name,
                agent_name: entry.agent_name,
            })
            if (historyRequestSeqRef.current !== requestSeq) return
            setHistoryItems(result.items || [])
            setSelectedActivationScriptId(currentId => {
                if (currentId !== null && result.items?.some(item => item.id === currentId)) {
                    return currentId
                }
                return entry.id
            })
        } catch {
            if (historyRequestSeqRef.current !== requestSeq) return
            setHistoryError(true)
            setHistoryItems([])
        } finally {
            if (historyRequestSeqRef.current !== requestSeq) return
            setHistoryLoading(false)
        }
    }

    const openHistoryModal = async (entry: AgentScriptHashEntry) => {
        const sourceCompanyName = requireSourceCompanyName(entry)
        if (!sourceCompanyName) return
        const requestSeq = ++historyRequestSeqRef.current
        setSelectedEntry(entry)
        setModalMode('history')
        setHistoryItems([])
        setHistoryLoading(true)
        setHistoryError(false)
        try {
            const result = await requestJson<ApiListResponse>('/api/backoffice/admin-page-agent-hashes/history', 'GET', undefined, {
                source_company_name: sourceCompanyName,
                entity_name: entry.entity_name,
                agent_name: entry.agent_name,
            })
            if (historyRequestSeqRef.current !== requestSeq) return
            setHistoryItems(result.items || [])
        } catch {
            if (historyRequestSeqRef.current !== requestSeq) return
            setHistoryError(true)
            setHistoryItems([])
        } finally {
            if (historyRequestSeqRef.current !== requestSeq) return
            setHistoryLoading(false)
        }
    }

    const closeModal = () => {
        historyRequestSeqRef.current += 1
        setModalMode(null)
        setSelectedEntry(null)
        setSelectedActivationScriptId(null)
        setDeactivateReason('')
        setHistoryItems([])
        setHistoryError(false)
    }

    const closeCreateModal = () => {
        setIsCreateModalOpen(false)
        setCreateConflict(null)
        setForm(emptyForm)
        resetHashState()
    }

    const closeCreateConflictModal = () => {
        setCreateConflict(null)
    }

    const requireSourceCompanyName = useCallback((entry?: AgentScriptHashEntry | null) => {
        const sourceCompanyName = entry?.company_name?.trim()
        if (!sourceCompanyName) {
            dispatch(setUserMsg({ msg: { type: 'danger', txt: 'Missing source company for this record' } }))
            return null
        }
        return sourceCompanyName
    }, [dispatch])

    const applySelectedHashVariant = useCallback((variant: HashVariant, preview: HashPreviewState) => {
        const nextHash = variant === 'raw' ? preview.rawHash : preview.normalizedHash
        if (!nextHash) return
        setHashPreview(prev => ({ ...prev, selectedVariant: variant }))
        setForm(prev => ({ ...prev, script_hash: nextHash }))
    }, [])

    const processScriptFile = useCallback(async (file: File) => {
        const requestSeq = ++fileRequestSeqRef.current
        setHashPreview({
            ...emptyHashPreview,
            fileName: file.name,
            isCalculating: true,
        })

        try {
            const [rawBuffer, rawText] = await Promise.all([
                file.arrayBuffer(),
                file.text(),
            ])
            const rawHash = await sha256Hex(rawBuffer)
            const normalizedBuffer = await new Blob([normalizeLineEndings(rawText)]).arrayBuffer()
            const normalizedHash = await sha256Hex(normalizedBuffer)
            const nextPreview: HashPreviewState = {
                fileName: file.name,
                rawHash,
                normalizedHash,
                isCalculating: false,
                error: null,
                selectedVariant: 'normalized',
            }
            if (requestSeq !== fileRequestSeqRef.current) return
            setHashPreview(nextPreview)
            setForm(prev => ({ ...prev, script_hash: normalizedHash }))
        } catch {
            if (requestSeq !== fileRequestSeqRef.current) return
            setHashPreview(prev => ({
                ...prev,
                isCalculating: false,
                error: 'Failed to calculate the hash for this file',
            }))
        }
    }, [])

    const handleScriptFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (!file) return
        await processScriptFile(file)
    }

    const handleScriptFileDrop = async (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        event.stopPropagation()
        const file = event.dataTransfer.files?.[0]
        if (!file) return
        await processScriptFile(file)
    }

    const submitCreate = async (sourceCompanyName?: string, skipConfirmation = false) => {
        if (!form.entity_name.trim() || !form.agent_name.trim() || !form.script_version.trim() || !form.script_hash.trim()) {
            dispatch(setUserMsg({ msg: { type: 'danger', txt: 'All fields are required' } }))
            return
        }

        if (!skipConfirmation) {
            const matchingPairItems = latestItems.filter(item =>
                normalizePairKey(item.entity_name) === normalizePairKey(form.entity_name)
                && normalizePairKey(item.agent_name) === normalizePairKey(form.agent_name)
            )
            const latestPairEntry = sourceCompanyName
                ? matchingPairItems.find(item => normalizePairKey(getDisplayCompanyName(item)) === normalizePairKey(sourceCompanyName))
                : matchingPairItems.length === 1 ? matchingPairItems[0] : null
            const confirmationMessage = latestPairEntry?.is_active
                ? `This will deactivate ${getDisplayCompanyName(latestPairEntry)} / ${latestPairEntry.entity_name} / ${latestPairEntry.agent_name} v${latestPairEntry.script_version} and register ${form.entity_name.trim()} / ${form.agent_name.trim()} v${form.script_version.trim()}. Continue?`
                : `This will register ${form.entity_name.trim()} / ${form.agent_name.trim()} v${form.script_version.trim()}. Continue?`
            if (!window.confirm(confirmationMessage)) {
                return
            }
        }

        setIsSubmitting(true)
        try {
            const payload = {
                ...form,
                ...(sourceCompanyName ? { source_company_name: sourceCompanyName } : {}),
            }
            const result = await requestJson<ApiEntryResponse>('/api/backoffice/admin-page-agent-hashes', 'POST', payload)
            dispatch(setUserMsg({
                msg: {
                    type: 'success',
                    txt: result.replaced_active_entry
                        ? `Created version ${result.entry.script_version} and replaced the previous active version`
                        : `Created version ${result.entry.script_version}`,
                },
            }))
            closeCreateModal()
            await loadItems()
        } catch (error: any) {
            if (error?.status === 409) {
                const conflict = await error.json?.().catch(() => null)
                const rawCandidates = Array.isArray(conflict?.candidates) ? conflict.candidates : []
                const candidates: ApiCreateConflictCandidate[] = []
                rawCandidates.forEach((candidate: unknown) => {
                    const sourceCompanyName = typeof candidate === 'string'
                        ? candidate.trim()
                        : String((candidate as ApiCreateConflictCandidate)?.source_company_name || '').trim()
                    if (!sourceCompanyName) return
                    if (candidates.some(item => item.source_company_name === sourceCompanyName)) return
                    candidates.push({ source_company_name: sourceCompanyName })
                })

                if (candidates.length > 0) {
                    setCreateConflict({
                        message: conflict?.message,
                        entity_name: conflict?.entity_name || form.entity_name.trim(),
                        candidates,
                    })
                    return
                }
            }

            const text = await error?.json?.().catch(() => null)
            dispatch(setUserMsg({ msg: { type: 'danger', txt: text?.message || 'Failed creating agent script version' } }))
        } finally {
            setIsSubmitting(false)
        }
    }

    const onCreate = async () => {
        await submitCreate()
    }

    const onResolveCreateConflict = async (sourceCompanyName: string) => {
        setCreateConflict(null)
        await submitCreate(sourceCompanyName)
    }

    const onDeactivate = async () => {
        if (!selectedEntry) return
        const sourceCompanyName = requireSourceCompanyName(selectedEntry)
        if (!sourceCompanyName) return
        if (!deactivateReason.trim()) {
            dispatch(setUserMsg({ msg: { type: 'danger', txt: 'Deactivation reason is required' } }))
            return
        }
        setIsSubmitting(true)
        try {
            await requestJson<ApiEntryResponse>('/api/backoffice/admin-page-agent-hashes/deactivate', 'PATCH', {
                id: selectedEntry.id,
                reason: deactivateReason.trim(),
                source_company_name: sourceCompanyName,
            })
            dispatch(setUserMsg({ msg: { type: 'success', txt: `Deactivated version ${selectedEntry.script_version}` } }))
            closeModal()
            await loadItems()
        } catch (error: any) {
            const text = await error?.json?.().catch(() => null)
            dispatch(setUserMsg({ msg: { type: 'danger', txt: text?.message || 'Failed deactivating agent script version' } }))
        } finally {
            setIsSubmitting(false)
        }
    }

    const onActivate = async () => {
        if (!selectedEntry) return
        const sourceCompanyName = requireSourceCompanyName(selectedEntry)
        if (!sourceCompanyName) return
        if (!selectedActivationScriptId) {
            dispatch(setUserMsg({ msg: { type: 'danger', txt: 'Please select a version to activate' } }))
            return
        }
        if (!deactivateReason.trim()) {
            dispatch(setUserMsg({ msg: { type: 'danger', txt: 'Activation reason is required' } }))
            return
        }
        setIsSubmitting(true)
        try {
            await requestJson<ApiEntryResponse>('/api/backoffice/admin-page-agent-hashes/activate', 'PATCH', {
                id: selectedActivationScriptId,
                reason: deactivateReason.trim(),
                source_company_name: sourceCompanyName,
            })
            const activatedEntry = historyItems.find(item => item.id === selectedActivationScriptId) ?? selectedEntry
            dispatch(setUserMsg({ msg: { type: 'success', txt: `Activated version ${activatedEntry.script_version}` } }))
            closeModal()
            await loadItems()
        } catch (error: any) {
            const text = await error?.json?.().catch(() => null)
            dispatch(setUserMsg({ msg: { type: 'danger', txt: text?.message || 'Failed activating agent script version' } }))
        } finally {
            setIsSubmitting(false)
        }
    }

    const latestItems = useMemo(() => {
        const latestByPair = new Map<string, AgentScriptHashEntry>()

        items.forEach(item => {
            const key = `${normalizePairKey(getDisplayCompanyName(item))}::${normalizePairKey(item.entity_name)}::${normalizePairKey(item.agent_name)}`
            const current = latestByPair.get(key)
            if (!current || isPreferredDisplayEntry(item, current)) {
                latestByPair.set(key, item)
            }
        })

        return Array.from(latestByPair.values()).sort((a, b) => {
            const entityDiff = a.entity_name.localeCompare(b.entity_name)
            if (entityDiff !== 0) return entityDiff
            const agentDiff = a.agent_name.localeCompare(b.agent_name)
            if (agentDiff !== 0) return agentDiff
            return (b.id || 0) - (a.id || 0)
        })
    }, [items])

    const sortedLatestItems = useMemo(() => {
            const getSortValue = (entry: AgentScriptHashEntry): string | number => {
            switch (sortKey) {
                case 'source':
                    return getDisplayCompanyName(entry)
                case 'entity':
                    return entry.entity_name
                case 'agent':
                    return entry.agent_name
                case 'version':
                    return entry.script_version
                case 'hash':
                    return entry.script_hash
                case 'implementedBy':
                    return entry.implemented_by
                case 'implementedAt':
                    return Date.parse(entry.implemented_at || '') || 0
                case 'status':
                    return entry.is_active ? 'Active' : 'Inactive'
                default:
                    return entry.entity_name
            }
        }

        const sorted = [...latestItems].sort((a, b) => {
            const aValue = getSortValue(a)
            const bValue = getSortValue(b)

            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return sortDirection === 'asc' ? aValue - bValue : bValue - aValue
            }

            const comparison = String(aValue).localeCompare(String(bValue), undefined, {
                numeric: true,
                sensitivity: 'base',
            })
            return sortDirection === 'asc' ? comparison : -comparison
        })

        return sorted
    }, [latestItems, sortDirection, sortKey])

    const handleSort = useCallback((nextKey: SortKey) => {
        setSortKey(currentKey => {
            if (currentKey === nextKey) {
                setSortDirection(currentDirection => (currentDirection === 'asc' ? 'desc' : 'asc'))
                return currentKey
            }
            setSortDirection('asc')
            return nextKey
        })
    }, [])

    const getAriaSort = useCallback((key: SortKey) => {
        if (sortKey !== key) return 'none'
        return sortDirection === 'asc' ? 'ascending' : 'descending'
    }, [sortDirection, sortKey])

    const entityNameSuggestions = useMemo(() => {
        return Array.from(new Set(items.map(item => item.entity_name).filter(Boolean))).sort((a, b) => a.localeCompare(b))
    }, [items])

    const getReplacementLabel = useCallback((replacedBy?: number | null) => {
        if (replacedBy == null) return '—'
        const replacedEntry = historyItems.find(historyItem => historyItem.id === replacedBy)
        if (!replacedEntry) return '—'
        return `${replacedEntry.entity_name} / ${replacedEntry.agent_name} v${replacedEntry.script_version}`
    }, [historyItems])

    const visibleHistoryItems = useMemo(() => {
        if (!selectedEntry) return historyItems
        return historyItems.filter(item => item.id !== selectedEntry.id)
    }, [historyItems, selectedEntry])

    return <div className="tablePageContainer agent-hashes-page">
        <div className="page-header agent-hashes-page__header">
            <p className="tableSubtitle">Global agent script hash registry across the main database and all silos.</p>
            <div className="page-header-actions agent-hashes-page__headerActions">
                <button className="add-btn" onClick={openCreateModal}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    Register Agent Script Hash
                </button>
            </div>
        </div>

        {isLoading && <Spinner />}
        {!isLoading && !hasError && (
            <div className="generic-table-container agent-hashes-page__tableWrap">
                <div className="table-scroll-area">
                <table className="generic-table agent-hashes-page__table">
                    <thead>
                        <tr>
                            <th aria-sort={getAriaSort('source')} onClick={() => handleSort('source')} tabIndex={0} onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    handleSort('source')
                                }
                            }}>
                                Source
                                {sortKey === 'source' && <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                            </th>
                            <th aria-sort={getAriaSort('entity')} onClick={() => handleSort('entity')} tabIndex={0} onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    handleSort('entity')
                                }
                            }}>
                                Entity
                                {sortKey === 'entity' && <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                            </th>
                            <th aria-sort={getAriaSort('agent')} onClick={() => handleSort('agent')} tabIndex={0} onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    handleSort('agent')
                                }
                            }}>
                                Agent
                                {sortKey === 'agent' && <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                            </th>
                            <th aria-sort={getAriaSort('version')} onClick={() => handleSort('version')} tabIndex={0} onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    handleSort('version')
                                }
                            }}>
                                Version
                                {sortKey === 'version' && <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                            </th>
                            <th aria-sort={getAriaSort('hash')} onClick={() => handleSort('hash')} tabIndex={0} onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    handleSort('hash')
                                }
                            }}>
                                Hash
                                {sortKey === 'hash' && <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                            </th>
                            <th aria-sort={getAriaSort('implementedBy')} onClick={() => handleSort('implementedBy')} tabIndex={0} onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    handleSort('implementedBy')
                                }
                            }}>
                                Implemented By
                                {sortKey === 'implementedBy' && <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                            </th>
                            <th aria-sort={getAriaSort('implementedAt')} onClick={() => handleSort('implementedAt')} tabIndex={0} onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    handleSort('implementedAt')
                                }
                            }}>
                                Implemented At
                                {sortKey === 'implementedAt' && <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                            </th>
                            <th aria-sort={getAriaSort('status')} onClick={() => handleSort('status')} tabIndex={0} onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    handleSort('status')
                                }
                            }}>
                                Status
                                {sortKey === 'status' && <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedLatestItems.map(item => (
                            <tr
                                key={item.id}
                                className={`agent-hashes-page__rowButton ${item.is_active ? 'is-active' : 'is-inactive'}`}
                                role="button"
                                tabIndex={0}
                                aria-label={`Open version details for ${getDisplayCompanyName(item)} ${item.entity_name} ${item.agent_name} ${item.script_version}`}
                                onClick={() => openHistoryModal(item)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault()
                                        openHistoryModal(item)
                                    }
                                }}
                            >
                                <td>{getDisplayCompanyName(item)}</td>
                                <td>{item.entity_name}</td>
                                <td>{item.agent_name}</td>
                                <td>{item.script_version}</td>
                                <td className="agent-hashes-page__hash">{item.script_hash}</td>
                                <td>{item.implemented_by}</td>
                                <td>{formatDateTime(item.implemented_at)}</td>
                                <td>
                                    <span className={`agent-hashes-page__status ${item.is_active ? 'is-active' : 'is-inactive'}`}>
                                        {item.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                            </tr>
                        ))}
                        {sortedLatestItems.length === 0 && (
                            <tr>
                                <td colSpan={8} className="agent-hashes-page__empty">No agent script hashes found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
                </div>
            </div>
        )}
        {!isLoading && hasError && <div className="error-hint">Failed to load data. Try refreshing the page or re-logging in.</div>}

        {isCreateModalOpen && (
            <FormModal
                header="Register Agent Script Hash"
                onCloseModal={closeCreateModal}
                actionBtnText="Register"
                onActionBtn={onCreate}
                isShowSpinner={isSubmitting}
            >
                <div className="form-section-card">
                    <div className="form-section-label">Script Hash Details</div>
                    <div className="form-row">
                        <div className="field">
                            <FormComboBox
                                label="Entity Name"
                                keyName="entity_name"
                                value={form.entity_name}
                                onChangeValue={entityName => setForm(prev => ({ ...prev, entity_name: entityName }))}
                                options={entityNameSuggestions}
                                placeholder="Type or choose an existing entity"
                            />
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="field">
                            <FormComboBox
                                label="Agent Name"
                                keyName="agent_name"
                                value={form.agent_name}
                                onChangeValue={agentName => setForm(prev => ({ ...prev, agent_name: agentName }))}
                                options={agentNameSuggestions}
                                placeholder="Type or choose an existing agent"
                            />
                        </div>
                        <div className="field">
                            <FormComboBox
                                label="Script Version"
                                keyName="script_version"
                                value={form.script_version}
                                onChangeValue={scriptVersion => setForm(prev => ({ ...prev, script_version: scriptVersion }))}
                                options={scriptVersionSuggestions}
                                placeholder="Type or choose an existing version"
                            />
                        </div>
                    </div>
                    <div className={`agent-hashes-page__uploadBox ${hashPreview.isCalculating ? 'is-processing' : ''}`}>
                        <input
                            ref={fileInputRef}
                            className="agent-hashes-page__fileInput"
                            type="file"
                            accept=".js,.ts,.tsx,.txt,.json,.py,.sh,.yaml,.yml,.md,.csv,.xml,text/plain,application/json"
                            onChange={handleScriptFileChange}
                        />
                        <div
                            className="agent-hashes-page__uploadSurface"
                            role="button"
                            tabIndex={0}
                            onClick={() => fileInputRef.current?.click()}
                            onDragOver={e => e.preventDefault()}
                            onDrop={handleScriptFileDrop}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    fileInputRef.current?.click()
                                }
                            }}
                        >
                            <div className="agent-hashes-page__uploadSurfaceIcon">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="17 8 12 3 7 8" />
                                    <line x1="12" y1="3" x2="12" y2="15" />
                                </svg>
                            </div>
                            <div className="agent-hashes-page__uploadSurfaceText">
                                <strong>Upload script file</strong>
                                <span>Drop a file here or click to choose one. The app will calculate raw and LF-normalized SHA-256 values.</span>
                            </div>
                        </div>

                        {hashPreview.fileName && (
                            <div className="agent-hashes-page__hashPanel">
                                <div className="agent-hashes-page__hashPanelHeader">
                                    <span className="agent-hashes-page__hashPanelFile">{hashPreview.fileName}</span>
                                    <span className="agent-hashes-page__hashPanelState">
                                        {hashPreview.isCalculating ? 'Calculating...' : 'Calculated'}
                                    </span>
                                </div>
                                {hashPreview.error && <div className="agent-hashes-page__hashPanelError">{hashPreview.error}</div>}
                                {!hashPreview.isCalculating && hashPreview.rawHash && hashPreview.normalizedHash && (
                                    <>
                                        <div className="agent-hashes-page__hashPair">
                                            <div className="agent-hashes-page__hashPairLabel">Raw SHA-256</div>
                                            <div className="agent-hashes-page__hashPairValue">{hashPreview.rawHash}</div>
                                        </div>
                                        <div className="agent-hashes-page__hashPair">
                                            <div className="agent-hashes-page__hashPairLabel">Normalized SHA-256 (LF)</div>
                                            <div className="agent-hashes-page__hashPairValue">{hashPreview.normalizedHash}</div>
                                        </div>
                                        <div className="agent-hashes-page__hashActions">
                                            <button
                                                type="button"
                                                className={`agent-hashes-page__hashAction ${hashPreview.selectedVariant === 'normalized' ? 'is-active' : ''}`}
                                                onClick={() => applySelectedHashVariant('normalized', hashPreview)}
                                            >
                                                Use normalized hash
                                            </button>
                                            <button
                                                type="button"
                                                className={`agent-hashes-page__hashAction ${hashPreview.selectedVariant === 'raw' ? 'is-active' : ''}`}
                                                onClick={() => applySelectedHashVariant('raw', hashPreview)}
                                            >
                                                Use raw hash
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="field">
                        <FormInputText
                            label="Script Hash"
                            keyName="script_hash"
                            isRequired={true}
                            value={form.script_hash}
                            onChange={e => setForm(prev => ({ ...prev, script_hash: e.target.value }))}
                            placeholder={hashPreview.isCalculating ? 'Calculating hash...' : 'Paste or edit the hash here'}
                        />
                    </div>
                    <p className="agent-hashes-page__modalHint">
                        Upload the script file to compute and register its approved SHA-256 hash.
                    </p>
                </div>
            </FormModal>
        )}

        {isCreateModalOpen && createConflict && (
            <FormModal
                header="Select Company"
                zIndex={320}
                onCloseModal={closeCreateConflictModal}
                hideActionBtn={true}
            >
                <div className="agent-hashes-page__modalBody">
                    <p className="agent-hashes-page__modalLead">
                        {createConflict.message || `The entity name "${createConflict.entity_name || form.entity_name.trim()}" exists in more than one company.`}
                    </p>
                    <p className="agent-hashes-page__modalHint">
                        Choose the company you want to register this hash under.
                    </p>
                    <div className="agent-hashes-page__companyChoiceList">
                        {createConflict.candidates.map(candidate => (
                            <button
                                key={candidate.source_company_name}
                                type="button"
                                className="agent-hashes-page__companyChoiceButton"
                                onClick={() => onResolveCreateConflict(candidate.source_company_name)}
                                disabled={isSubmitting}
                            >
                                <span>{candidate.source_company_name}</span>
                                <small>Register under this company</small>
                            </button>
                        ))}
                    </div>
                </div>
            </FormModal>
        )}

        {modalMode === 'deactivate' && selectedEntry && (
            <FormModal
                header="Deactivate Version"
                onCloseModal={closeModal}
                actionBtnText={isSubmitting ? 'Saving...' : 'Deactivate'}
                onActionBtn={onDeactivate}
                isShowSpinner={isSubmitting}
            >
                <div className="agent-hashes-page__modalBody">
                    <p className="agent-hashes-page__modalLead">
                        {selectedEntry.entity_name} / {selectedEntry.agent_name} v{selectedEntry.script_version}
                    </p>
                    <label className="agent-hashes-page__modalField">
                        <span>Reason *</span>
                        <textarea
                            rows={5}
                            value={deactivateReason}
                            onChange={e => setDeactivateReason(e.target.value)}
                            aria-required="true"
                        />
                    </label>
                </div>
            </FormModal>
        )}

        {modalMode === 'activate' && selectedEntry && (
            <FormModal
                header="Activate Version"
                onCloseModal={closeModal}
                actionBtnText={isSubmitting ? 'Saving...' : 'Activate'}
                onActionBtn={onActivate}
                isShowSpinner={isSubmitting}
            >
                <div className="agent-hashes-page__modalBody">
                    <p className="agent-hashes-page__modalLead">
                        Select the exact version to restore for {selectedEntry.entity_name} / {selectedEntry.agent_name}.
                    </p>
                    <label className="agent-hashes-page__modalField">
                        <span>Version</span>
                        <select
                            value={selectedActivationScriptId ?? selectedEntry.id}
                            onChange={e => setSelectedActivationScriptId(Number(e.target.value))}
                            disabled={historyLoading}
                        >
                            {(historyItems.length > 0 ? historyItems : [selectedEntry]).map(item => (
                                <option key={item.id} value={item.id}>
                                    {item.script_version} {item.is_active ? '(active)' : '(inactive)'} - {formatDateTime(item.implemented_at)}
                                </option>
                            ))}
                        </select>
                    </label>
                    {historyLoading && <Spinner />}
                    {!historyLoading && historyError && (
                        <div className="error-hint">Failed to load version history. You can still activate the selected row.</div>
                    )}
                    {!historyLoading && !historyError && historyItems.length > 0 && (
                        <div className="agent-hashes-page__modalHint">
                            This list shows all versions for the selected agent. Choose the exact one to reactivate.
                        </div>
                    )}
                    <label className="agent-hashes-page__modalField">
                        <span>Reason *</span>
                        <textarea
                            rows={5}
                            value={deactivateReason}
                            onChange={e => setDeactivateReason(e.target.value)}
                            aria-required="true"
                        />
                    </label>
                </div>
            </FormModal>
        )}

        {modalMode === 'history' && selectedEntry && (
            <FormModal
                header="Version Details"
                onCloseModal={closeModal}
                hideActionBtn={true}
                actionBtnText="Close"
                onActionBtn={closeModal}
                isShowSpinner={historyLoading}
            >
                <div className="agent-hashes-page__modalBody agent-hashes-page__modalBody--history">
                        <div className="agent-hashes-page__modalHeader">
                            <div>
                                <div className="agent-hashes-page__modalKicker">Script registry entry</div>
                                <h3>{getDisplayCompanyName(selectedEntry)} / {selectedEntry.entity_name} / {selectedEntry.agent_name}</h3>
                            </div>
                        </div>
                    <div className="agent-hashes-page__detailsCard">
                        <div className="agent-hashes-page__detailsCardHeader">
                            <div>
                                <div className="agent-hashes-page__detailsCardKicker">Script version</div>
                                <div className="agent-hashes-page__detailsCardTitle">{selectedEntry.script_version}</div>
                            </div>
                            <div className="agent-hashes-page__detailsCardActions">
                                <span className={`agent-hashes-page__status ${selectedEntry.is_active ? 'is-active' : 'is-inactive'}`}>
                                    {selectedEntry.is_active ? 'Active' : 'Inactive'}
                                </span>
                                {selectedEntry.is_active ? (
                                    <button
                                        type="button"
                                        className="agent-hashes-page__secondaryButton agent-hashes-page__secondaryButton--danger"
                                        onClick={() => openDeactivateModal(selectedEntry)}
                                    >
                                        Deactivate
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="agent-hashes-page__secondaryButton"
                                        onClick={() => openActivateModal(selectedEntry)}
                                    >
                                        Activate
                                    </button>
                                )}
                            </div>
                        </div>
                        <dl className="agent-hashes-page__detailsGrid">
                            <div>
                                <dt>Source company</dt>
                                <dd>{getDisplayCompanyName(selectedEntry)}</dd>
                            </div>
                            <div>
                                <dt>Hash</dt>
                                <dd>{selectedEntry.script_hash}</dd>
                            </div>
                            <div>
                                <dt>Implemented by</dt>
                                <dd>{selectedEntry.implemented_by}</dd>
                            </div>
                            <div>
                                <dt>Implemented at</dt>
                                <dd>{formatDateTime(selectedEntry.implemented_at)}</dd>
                            </div>
                        </dl>
                    </div>
                    {!historyLoading && historyError && <div className="error-hint">Failed to load version history.</div>}
                    {!historyLoading && !historyError && (
                        <div className="agent-hashes-page__history">
                            {visibleHistoryItems.map(item => (
                                <div key={item.id} className={`agent-hashes-page__historyItem ${item.is_active ? 'is-active' : ''}`}>
                                    <div className="agent-hashes-page__historyHeader">
                                    <div className="agent-hashes-page__historyVersion">
                                            <strong>{getDisplayCompanyName(item)} / {item.script_version}</strong>
                                        </div>
                                        <div className="agent-hashes-page__historyActions">
                                            <span className={`agent-hashes-page__status ${item.is_active ? 'is-active' : 'is-inactive'}`}>
                                                {item.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                            {item.is_active ? (
                                                <button
                                                    type="button"
                                                    className="agent-hashes-page__secondaryButton agent-hashes-page__secondaryButton--danger"
                                                    onClick={() => openDeactivateModal(item)}
                                                >
                                                    Deactivate
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="agent-hashes-page__secondaryButton"
                                                    onClick={() => openActivateModal(item)}
                                                >
                                                    Activate
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <dl className="agent-hashes-page__historyDetails">
                                        <div>
                                            <dt>Hash</dt>
                                            <dd>{item.script_hash}</dd>
                                        </div>
                                        <div>
                                            <dt>Implemented by</dt>
                                            <dd>{item.implemented_by}</dd>
                                        </div>
                                        <div>
                                            <dt>Implemented at</dt>
                                            <dd>{formatDateTime(item.implemented_at)}</dd>
                                        </div>
                                        <div>
                                            <dt>Deactivated at</dt>
                                            <dd>{formatDateTime(item.deactivated_at)}</dd>
                                        </div>
                                        <div>
                                            <dt>Replaced by</dt>
                                            <dd>{getReplacementLabel(item.replaced_by)}</dd>
                                        </div>
                                    </dl>
                                </div>
                            ))}
                            {visibleHistoryItems.length === 0 && <div className="agent-hashes-page__historyEmpty">No previous versions found.</div>}
                        </div>
                    )}
                </div>
            </FormModal>
        )}
    </div>
}
